import React from 'react';
import RichText from '../RichText';
import Image from 'next/image';
import { getImageDataWithHttps } from '../../utils/contentful';
import { IMultiUpListFields } from '../../../types/contentful';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Block, Inline } from '@contentful/rich-text-types/dist/types/types';
import { BLOCKS } from '@contentful/rich-text-types';
import Lottie from 'lottie-react';
import { Asset } from 'contentful';
import Link from '../Link';

interface Props extends IMultiUpListFields {
  sysId?: string;
}

export function MultiUpList({ headline, items, sysId }: Props) {
  const overrideOptions: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return <p className="font-normal">{children}</p>;
    }
  };

  return (
    <section
      className="flex flex-col items-center gap-6"
      id={sysId}
      data-testid="multi-up-list"
    >
      {headline && (
        <div className="flex items-center justify-center gap-[10px] self-stretch px-6 tablet:items-start tablet:p-4">
          <h2 className="heading-2 max-w-[934px] text-center">{headline}</h2>
        </div>
      )}
      <div className="flex flex-col justify-center gap-6 self-stretch px-3 tablet:flex-row tablet:items-start tablet:gap-8 tablet:px-10 desktop:flex desktop:px-15">
        {items.map(({ sys, fields }) => {
          return (
            <div
              key={sys.id}
              data-testid="multi-up-list-item"
              className="flex w-full flex-1 flex-row items-center justify-center gap-5 tablet:flex-col tablet:justify-normal tablet:self-stretch desktop:w-full desktop:max-w-[280px] desktop:px-5"
            >
              {fields.animatedImage && (
                <Lottie
                  className="aspect-square w-[116px] object-contain tablet:w-[220px]"
                  animationData={fields.animatedImage}
                  loop={true}
                />
              )}
              {fields.image && !fields.animatedImage && (
                <Image
                  {...getImageDataWithHttps(fields.image as Asset)}
                  loader={({ src }) => src}
                  unoptimized={true}
                  className="aspect-square w-[116px] object-contain tablet:w-[220px]"
                  width={fields.image.fields.file.details.image?.width}
                  height={fields.image.fields.file.details.image?.height}
                />
              )}
              <div className="flex flex-col items-start justify-center gap-4 self-stretch tablet:items-center tablet:justify-normal tablet:gap-6">
                <div className="flex flex-col items-start gap-2 self-stretch tablet:items-center">
                  {fields.headline && (
                    <h3 className="text-left text-xl font-medium leading-[120%] tablet:text-center tablet:text-2xl">
                      {fields.headline}
                    </h3>
                  )}
                  <div className="h-fit text-left tablet:text-center">
                    {fields.body && (
                      <RichText
                        data={fields.body}
                        overrideOptions={overrideOptions}
                      />
                    )}
                  </div>
                </div>
                {fields.jumplink && (
                  <Link
                    className="body-link group"
                    href={
                      fields.jumplink.fields.hasAffiliateLink
                        ? `/affiliates${fields.jumplink.fields.url}`
                        : fields.jumplink.fields.url
                    }
                    target={fields.jumplink.fields.openInNewTab ? '_blank' : ''}
                    rel="noopener noreferrer"
                  >
                    {fields.jumplink.fields.title}{' '}
                    <span className="absolute left-full ml-2 opacity-0 transition-opacity group-active:opacity-100">
                      →
                    </span>
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
